(function () {
  'use strict';

  var cache = new Map();
  var requestQueue = [];
  var activeRequests = 0;
  var MAX_CONCURRENT_REQUESTS = 8;
  var STORAGE_KEY_PREFIX = 'storefront-discount-prices:';
  var STORAGE_TTL_MS = 5 * 60 * 1000;
  var standardGridSyncFrame = null;

  var STYLE_CONFIG = {
    default: {
      currentSelector: '[data-sale-price]',
      compareSelector: '[data-compare-price]',
      compareGroupSelector: '[data-compare-group]',
      badgeSelector: '[data-discount-badge]',
      badgeScopeSelector: '',
      saleClass: ''
    },
    'tnf-pccarousel': {
      currentSelector: '.tnf-pccarousel__current-price',
      compareSelector: '.tnf-pccarousel__compare-price',
      compareGroupSelector: '',
      badgeSelector: '[data-discount-badge]',
      badgeScopeSelector: '[data-product-card]',
      saleClass: 'tnf-pccarousel__prices--sale'
    }
  };

  function getConfig(container) {
    var style = container.getAttribute('data-price-style') || 'default';
    return STYLE_CONFIG[style] || STYLE_CONFIG.default;
  }

  function formatMoney(amount, locale, currency) {
    var numericAmount = parseFloat(amount || '0');
    if (Number.isNaN(numericAmount)) return '';
    // El peso colombiano no usa centavos: el 5% extra puede dar 322.952,50 y se muestra 322.953.
    if (String(currency || '').toUpperCase() === 'COP') numericAmount = Math.round(numericAmount);
    var isWholeAmount = Math.abs(numericAmount % 1) < 0.00001;
    var fractionDigits = isWholeAmount ? 0 : 2;

    try {
      return '$' + new Intl.NumberFormat(locale || 'es-CO', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(numericAmount);
    } catch (error) {
      return '$' + new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(numericAmount);
    }
  }

  function toPriceCents(amount) {
    var numericAmount = parseFloat(amount || '0');
    if (Number.isNaN(numericAmount) || numericAmount <= 0) return '0';
    return String(Math.round(numericAmount * 100));
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function isRetryableStatus(status) {
    return status === 429 || status === 500 || status === 503;
  }

  function parseCurrencyUnits(value) {
    if (value == null) return null;

    var digits = String(value).replace(/[^\d]/g, '');
    if (!digits) return null;

    var parsedValue = parseInt(digits, 10);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  function getActivePriceRangeCents() {
    var url = new URL(window.location.href);
    var minUnits = parseCurrencyUnits(url.searchParams.get('filter.v.price.gte'));
    var maxUnits = parseCurrencyUnits(url.searchParams.get('filter.v.price.lte'));

    return {
      min: minUnits == null ? null : minUnits * 100,
      max: maxUnits == null ? null : maxUnits * 100,
    };
  }

  function hasActivePriceRange(priceRange) {
    return priceRange.min !== null || priceRange.max !== null;
  }

  function getCurrentSortValue() {
    var url = new URL(window.location.href);
    var urlSortValue = url.searchParams.get('sort_by');
    if (urlSortValue) return urlSortValue;

    var checkedInput = document.querySelector('input[name="sort_by"]:checked');
    if (checkedInput instanceof HTMLInputElement) return checkedInput.value;

    var selectedOption = document.querySelector('select[name="sort_by"] option:checked');
    if (selectedOption instanceof HTMLOptionElement) return selectedOption.value;

    return '';
  }

  function isPriceSortValue(sortValue) {
    return sortValue === 'price-ascending' || sortValue === 'price-descending';
  }

  function getStandardProductGrids() {
    return Array.from(document.querySelectorAll('.product-grid')).filter(function (grid) {
      return grid instanceof HTMLElement && !grid.closest('.tnf-mc') && !!grid.querySelector('[data-discount-price]');
    });
  }

  function registerStandardGridIndices(grid) {
    if (!(grid instanceof HTMLElement)) return;

    if (typeof grid.__discountSortIndexCounter !== 'number') {
      grid.__discountSortIndexCounter = 0;
    }

    Array.from(grid.children).forEach(function (child) {
      if (!(child instanceof HTMLElement) || !child.matches('.product-grid__item')) return;
      if (child.hasAttribute('data-discount-sort-index')) return;

      child.setAttribute('data-discount-sort-index', String(grid.__discountSortIndexCounter));
      grid.__discountSortIndexCounter += 1;
    });
  }

  function getGridItemPriceCents(item) {
    if (!(item instanceof HTMLElement)) return Number.POSITIVE_INFINITY;

    var priceContainer = item.querySelector('[data-discount-price]');
    if (!(priceContainer instanceof HTMLElement)) return Number.POSITIVE_INFINITY;

    var currentPriceCents = parseInt(priceContainer.getAttribute('data-current-price-cents') || '0', 10);
    if (Number.isFinite(currentPriceCents) && currentPriceCents > 0) {
      return currentPriceCents;
    }

    return Number.POSITIVE_INFINITY;
  }

  function itemMatchesPriceRange(item, priceRange) {
    if (!hasActivePriceRange(priceRange)) return true;

    var currentPriceCents = getGridItemPriceCents(item);
    if (!Number.isFinite(currentPriceCents) || currentPriceCents === Number.POSITIVE_INFINITY) return true;

    if (priceRange.min !== null && currentPriceCents < priceRange.min) return false;
    if (priceRange.max !== null && currentPriceCents > priceRange.max) return false;
    return true;
  }

  function applyStandardGridPriceRange(grid) {
    if (!(grid instanceof HTMLElement)) return;

    var priceRange = getActivePriceRangeCents();
    Array.from(grid.children).forEach(function (child) {
      if (!(child instanceof HTMLElement) || !child.matches('.product-grid__item')) return;

      var matchesRange = itemMatchesPriceRange(child, priceRange);
      child.style.display = matchesRange ? '' : 'none';
    });
  }

  function applyStandardGridPriceSort(grid) {
    if (!(grid instanceof HTMLElement)) return;

    var sortValue = getCurrentSortValue();
    if (!isPriceSortValue(sortValue)) return;

    var items = Array.from(grid.children).filter(function (child) {
      return child instanceof HTMLElement && child.matches('.product-grid__item');
    });

    if (items.length < 2) return;

    items.sort(function (leftItem, rightItem) {
      var leftPrice = getGridItemPriceCents(leftItem);
      var rightPrice = getGridItemPriceCents(rightItem);

      if (leftPrice !== rightPrice) {
        return sortValue === 'price-descending' ? rightPrice - leftPrice : leftPrice - rightPrice;
      }

      var leftIndex = parseInt(leftItem.getAttribute('data-discount-sort-index') || '0', 10);
      var rightIndex = parseInt(rightItem.getAttribute('data-discount-sort-index') || '0', 10);
      return leftIndex - rightIndex;
    });

    var fragment = document.createDocumentFragment();
    items.forEach(function (item) {
      fragment.appendChild(item);
    });
    grid.appendChild(fragment);
  }

  function syncStandardProductGrids() {
    getStandardProductGrids().forEach(function (grid) {
      registerStandardGridIndices(grid);
      applyStandardGridPriceSort(grid);
      applyStandardGridPriceRange(grid);
    });
  }

  function scheduleStandardGridSync() {
    if (typeof standardGridSyncFrame === 'number') {
      cancelAnimationFrame(standardGridSyncFrame);
    }

    standardGridSyncFrame = requestAnimationFrame(function () {
      standardGridSyncFrame = null;
      syncStandardProductGrids();
    });
  }

  function normalizeVariantId(variantId) {
    if (variantId == null) return '';
    return String(variantId).trim();
  }

  function isLikelyVariantId(variantId) {
    return /^[0-9]{8,}$/.test(normalizeVariantId(variantId));
  }

  function runQueued(task) {
    return new Promise(function (resolve, reject) {
      requestQueue.push({ task: task, resolve: resolve, reject: reject });
      drainQueue();
    });
  }

  function drainQueue() {
    while (activeRequests < MAX_CONCURRENT_REQUESTS && requestQueue.length > 0) {
      var next = requestQueue.shift();
      if (!next) return;

      activeRequests += 1;

      Promise.resolve()
        .then(next.task)
        .then(next.resolve, next.reject)
        .finally(function () {
          activeRequests -= 1;
          drainQueue();
        });
    }
  }

  function getStorage() {
    try {
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  function readStoredPayload(variantId) {
    var storage = getStorage();
    if (!storage) return null;

    try {
      var rawValue = storage.getItem(STORAGE_KEY_PREFIX + variantId);
      if (!rawValue) return null;

      var parsed = JSON.parse(rawValue);
      if (!parsed || !parsed.payload || !parsed.expiresAt) return null;
      if (parsed.expiresAt <= Date.now()) {
        storage.removeItem(STORAGE_KEY_PREFIX + variantId);
        return null;
      }

      return parsed.payload;
    } catch (error) {
      return null;
    }
  }

  function storePayload(variantId, payload) {
    var storage = getStorage();
    if (!storage || !payload) return;

    try {
      storage.setItem(
        STORAGE_KEY_PREFIX + variantId,
        JSON.stringify({
          expiresAt: Date.now() + STORAGE_TTL_MS,
          payload: payload,
        })
      );
    } catch (error) {
      // Ignore storage quota/privacy failures.
    }
  }

  function fetchDiscountJson(variantId, attempt) {
    return fetch('/apps/discounts?variant_id=' + encodeURIComponent(variantId), {
      headers: { Accept: 'application/json' },
    }).then(function (response) {
      if (!response.ok) {
        if (attempt < 2 && isRetryableStatus(response.status)) {
          return delay(250 * (attempt + 1)).then(function () {
            return fetchDiscountJson(variantId, attempt + 1);
          });
        }

        throw new Error('HTTP ' + response.status);
      }

      return response.json();
    });
  }

  function fetchDiscountBatchJson(variantIds, attempt) {
    return fetch('/apps/discounts?variant_ids=' + encodeURIComponent(variantIds.join(',')), {
      headers: { Accept: 'application/json' },
    }).then(function (response) {
      if (!response.ok) {
        if (attempt < 2 && isRetryableStatus(response.status)) {
          return delay(250 * (attempt + 1)).then(function () {
            return fetchDiscountBatchJson(variantIds, attempt + 1);
          });
        }

        throw new Error('HTTP ' + response.status);
      }

      return response.json();
    });
  }

  function fetchDiscountData(variantId) {
    var normalizedVariantId = normalizeVariantId(variantId);
    if (!isLikelyVariantId(normalizedVariantId)) return Promise.resolve(null);

    var storedPayload = readStoredPayload(normalizedVariantId);
    if (storedPayload) {
      cache.set(normalizedVariantId, Promise.resolve(storedPayload));
      return cache.get(normalizedVariantId);
    }

    if (!cache.has(normalizedVariantId)) {
      cache.set(
        normalizedVariantId,
        runQueued(function () {
          return fetchDiscountJson(normalizedVariantId, 0);
        })
          .then(function (payload) {
            storePayload(normalizedVariantId, payload);
            return payload;
          })
          .catch(function (error) {
            cache.delete(normalizedVariantId);
            throw error;
          })
      );
    }

    return cache.get(normalizedVariantId);
  }

  function primePayload(variantId, payload) {
    if (!isLikelyVariantId(variantId) || !payload || payload.error) return;
    cache.set(variantId, Promise.resolve(payload));
    storePayload(variantId, payload);
  }

  function fetchDiscountBatch(variantIds) {
    var normalizedVariantIds = Array.from(new Set(variantIds.map(normalizeVariantId).filter(isLikelyVariantId)));
    if (!normalizedVariantIds.length) return Promise.resolve({ results: {} });

    var idsNeedingFetch = normalizedVariantIds.filter(function (variantId) {
      if (cache.has(variantId)) return false;

      var storedPayload = readStoredPayload(variantId);
      if (storedPayload) {
        cache.set(variantId, Promise.resolve(storedPayload));
        return false;
      }

      return true;
    });

    if (!idsNeedingFetch.length) {
      return Promise.resolve({ results: {} });
    }

    return runQueued(function () {
      return fetchDiscountBatchJson(idsNeedingFetch, 0);
    }).then(function (payload) {
      var results = payload && payload.results ? payload.results : {};

      Object.keys(results).forEach(function (variantId) {
        primePayload(variantId, results[variantId]);
      });

      return payload;
    });
  }

  function setCompareVisibility(container, config, isVisible, compareText) {
    var compareEl = container.querySelector(config.compareSelector);
    var compareTarget = config.compareGroupSelector
      ? container.querySelector(config.compareGroupSelector)
      : compareEl;

    if (!compareEl) return;

    compareEl.textContent = isVisible ? compareText : '';

    if (compareTarget) {
      compareTarget.toggleAttribute('hidden', !isVisible);
    }

    if (config.saleClass) {
      container.classList.toggle(config.saleClass, isVisible);
    }
  }

  function getSaleBadgeText(container, pricing) {
    var saleLabel = container.getAttribute('data-sale-label') || 'SALE';
    var original = parseFloat(pricing && pricing.original ? pricing.original : '0');
    var discounted = parseFloat(pricing && pricing.discounted ? pricing.discounted : '0');

    if (original > 0 && discounted > 0 && discounted < original) {
      var percentOff = Math.round(((original - discounted) / original) * 100);
      if (percentOff > 0) {
        return '-' + percentOff + '%';
      }
    }

    return saleLabel;
  }

  function resolveBadgeElement(container, config) {
    if (!config.badgeSelector) return null;

    var badgeRoot = container;
    if (config.badgeScopeSelector) {
      badgeRoot = container.closest(config.badgeScopeSelector) || container;
    }

    return badgeRoot.querySelector(config.badgeSelector);
  }

  function setBadgeVisibility(container, config, isVisible, badgeText) {
    var badgeEl = resolveBadgeElement(container, config);
    if (!badgeEl) return;

    badgeEl.textContent = isVisible ? badgeText : '';
    badgeEl.toggleAttribute('hidden', !isVisible);
  }

  function updatePriceContainer(container, payload) {
    if (!container || !payload || !payload.variant || !payload.variant.pricing) return;

    var config = getConfig(container);
    var locale = container.getAttribute('data-locale') || document.documentElement.lang || 'es-CO';
    var currency = container.getAttribute('data-currency') || 'COP';
    var pricing = payload.variant.pricing;
    var currentEl = container.querySelector(config.currentSelector);

    if (!currentEl) return;

    var original = pricing.original;
    var compareAt = pricing.compareAt;
    var discounted = pricing.discounted;
    var effectivePrice = discounted || original;
    var compareSource = null;

    if (discounted) {
      compareSource = original;
    } else if (compareAt && parseFloat(compareAt) > parseFloat(original)) {
      compareSource = compareAt;
    }

    currentEl.textContent = formatMoney(effectivePrice, locale, currency);
    container.setAttribute('data-current-price-cents', toPriceCents(effectivePrice));
    setCompareVisibility(container, config, !!compareSource, compareSource ? formatMoney(compareSource, locale, currency) : '');
    setBadgeVisibility(container, config, !!compareSource, compareSource ? getSaleBadgeText(container, pricing) : '');
    container.__storefrontDiscountHydrated = true;

    container.dispatchEvent(new CustomEvent('storefront-discount-prices:updated', {
      bubbles: true,
      detail: {
        variantId: payload.variant.id,
        pricing: pricing,
      }
    }));
  }

  function updateContainersByVariantId(root, variantId, payload) {
    var scope = root instanceof Element || root instanceof Document ? root : document;
    scope.querySelectorAll('[data-discount-price][data-variant-id="' + variantId + '"]').forEach(function (container) {
      updatePriceContainer(container, payload);
    });
  }

  function refreshContainer(container, variantId) {
    if (!(container instanceof HTMLElement)) return Promise.resolve();

    var resolvedVariantId = normalizeVariantId(variantId || container.getAttribute('data-variant-id'));
    if (!isLikelyVariantId(resolvedVariantId)) return Promise.resolve();

    container.setAttribute('data-variant-id', resolvedVariantId);

    return fetchDiscountData(resolvedVariantId)
      .then(function (payload) {
        updatePriceContainer(container, payload);
      })
      .catch(function (error) {
        console.error('[storefront-discount-prices]', error);
      });
  }

  function refreshCard(card, variantId) {
    if (!(card instanceof HTMLElement)) return Promise.resolve();
    var priceContainer = card.querySelector('[data-discount-price]');
    if (!priceContainer) return Promise.resolve();
    return refreshContainer(priceContainer, variantId);
  }

  function refreshAll(root) {
    var scope = root instanceof Element || root instanceof Document ? root : document;
    var containers = Array.from(scope.querySelectorAll('[data-discount-price]'));
    var pendingVariantIds = [];

    containers.forEach(function (container) {
      var variantId = normalizeVariantId(container.getAttribute('data-variant-id'));
      if (!isLikelyVariantId(variantId)) return;

      var cachedPayload = readStoredPayload(variantId);
      if (cachedPayload) {
        primePayload(variantId, cachedPayload);
        updatePriceContainer(container, cachedPayload);
        return;
      }

      pendingVariantIds.push(variantId);
    });

    fetchDiscountBatch(pendingVariantIds).then(function (payload) {
      var results = payload && payload.results ? payload.results : {};

      Object.keys(results).forEach(function (variantId) {
        if (results[variantId] && !results[variantId].error) {
          updateContainersByVariantId(scope, variantId, results[variantId]);
        }
      });
    }).catch(function (error) {
      console.error('[storefront-discount-prices]', error);
    });
  }

  function isObservedContainerHydrated(container) {
    return container instanceof HTMLElement && container.__storefrontDiscountHydrated === true;
  }

  function refreshObservedContainer(container) {
    if (!(container instanceof HTMLElement) || isObservedContainerHydrated(container)) return;
    refreshContainer(container);
  }

  function resolveScopedPriceContainer(target) {
    var scopedRoot = target.closest('[data-product-card], product-card, .shopify-section, [data-section-id]');
    if (!scopedRoot) return document.querySelector('[data-discount-price]');
    return scopedRoot.querySelector('[data-discount-price]');
  }

  function handleVariantSelection(event) {
    var target = event.target;
    if (!(target instanceof HTMLElement)) return;

    var variantId = target.getAttribute('data-variant-id');

    if (!variantId && target instanceof HTMLSelectElement) {
      var selectedOption = target.selectedOptions && target.selectedOptions[0];
      variantId = selectedOption ? selectedOption.getAttribute('data-variant-id') : '';
    }

    if (!variantId && target instanceof HTMLInputElement && target.checked) {
      variantId = target.getAttribute('data-variant-id');
    }

    if (!isLikelyVariantId(variantId)) return;

    var priceContainer = resolveScopedPriceContainer(target);
    if (!priceContainer) return;

    refreshContainer(priceContainer, variantId);
  }

  function observeDynamicPrices() {
    if (typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof Element)) return;

          if (node.matches('[data-discount-price]')) {
            refreshObservedContainer(node);
          }

          node.querySelectorAll('[data-discount-price]').forEach(function (container) {
            refreshObservedContainer(container);
          });
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  window.StorefrontDiscountPrices = {
    refreshAll: refreshAll,
    refreshCard: refreshCard,
    refreshContainer: refreshContainer,
    primePayload: primePayload,
    clearCache: function () {
      cache.clear();
    },
  };

  function initializeStorefrontDiscountPrices() {
    refreshAll(document);
    observeDynamicPrices();
    scheduleStandardGridSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeStorefrontDiscountPrices, { once: true });
  } else {
    initializeStorefrontDiscountPrices();
  }

  document.addEventListener('change', handleVariantSelection);
  document.addEventListener('storefront-discount-prices:updated', scheduleStandardGridSync);
  document.addEventListener('shopify:section:load', function (event) {
    refreshAll(event.target);
    scheduleStandardGridSync();
  });
})();